import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Grid,
  Typography,
  Paper,
  Box,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  Container,
} from "@mui/material";
import HttpClient, { Methods } from "../../../../services/HttpClient";
import ProgressIndicator from "../../../ProgressIndicator";
import Button from "@mui/material/Button";
import { formatDisplayDate } from "../../../../util/dateFormat";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PhotoGallery from "./PhotoGallery";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import MailIcon from "@mui/icons-material/Mail";

// Media queries
import { useMediaQuery } from "react-responsive";
import { MediaQueries } from "../../../MediaQueries";
import { appRoutes } from "../../../../constants/AppRoutes";
import { endpoints } from "../../../../constants/Endpoints";
import { useRef } from "react";
import Swal from "sweetalert2";
import { fileCategories } from "../../../../constants/FileTypes";

export function RentalPriceCalculationDetailView(props) {
  const isTabletOrMobileDevice = useMediaQuery(MediaQueries.MobileDevice);
  const [isMounted, setMounted] = useState(true);
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const styles = {
    defaultButton: {
      marginLeft: "20px",
      backgroundColor: "#595959",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "red !important",
      },
    },
    saveButton: {
      backgroundColor: "#eb008b",
      marginLeft: "20px",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    mailButton: {
      backgroundColor: "#ec2027",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "yellow !important",
      },
    },
    inactive: {
      color: "#ec2027",
      fontWeight: "bold",
    },
    active: {
      color: "green",
      fontWeight: "bold",
    },
    profilePicture: {
      borderRadius: "50%",
      width: "100%",
      display: "block",
      margin: "0 auto",
      minWidth: "100px",
      maxWidth: "140px",
      textAlign: "center",
    },
    alignRight: {
      textAlign: "right",
    },
    marginTop: {
      marginTop: "20px",
    },
  };

  const thumbnailStyle = () => {
    return {
      width: "auto",
      height: "auto",
    };
  };

  const navigate = useNavigate();
  const { id } = useParams();
  const formRef = useRef();

  const textFieldVariant = "outlined";

  const getData = useCallback(async () => {
    const response = await HttpClient.doRequest(
      Methods.GET,
      endpoints.lead.getRentalPriceCalculation + id
    );

    return await response.json();
  }, [props]);

  useEffect(() => {
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });

    return () => {
      setMounted(false);
    };
  }, [isMounted, getData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    sendData().then((s) => {
      if (s) {
        navigate(appRoutes.shb.lead.overview);
      } else {
        setLoading(false);
      }
    });
  };

  const sendData = async () => {
    const response = await HttpClient.doRequest(
      Methods.PUT,
      endpoints.lead.updateRentalPriceCalculation + id,
      data
    );

    if (response.ok) return true;
    else return false;
  };

  const archiveRentalPriceCalculation = async () => {
    const response = await HttpClient.doRequest(
      Methods.DELETE,
      endpoints.lead.removeRentalPriceCalculation + data.leadId
    );

    if (response.ok) {
      navigate(appRoutes.shb.lead.overview);
    }
  };

  const handleDateChange = (name, date) => {
    if (date != null) {
      var _date = new Date(date);
      var year = _date.getFullYear();
      var month = _date.getMonth();
      var day = _date.getDate();
      var newDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));

      if (isMounted) {
        setData({ ...data, [name]: newDate.toISOString() });
      }
    } else if (isMounted) {
      setData({ ...data, [name]: null });
    }
  };

  const handleInputChange = (e) => {
    if (isMounted) {
      const { name, value, type } = e.target;
      setData({
        ...data,
        [name]:
          type === "number" ? (value !== "" ? parseInt(value) : 0) : value,
      });
    }
  };

  const sendAccountActivationEmail = async () => {
    setLoading(true);

    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.lead.sendRentalPriceCalculationActivationMail + data.id
    );

    const message = await response.json();

    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        onOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });

      Toast.fire({
        icon: message.icon,
        title: message.title,
      });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }
    setLoading(false);
  };

  return (
    <Fragment>
      {isLoading ? (
        <div style={{ width: "100%", textAlign: "center" }}>
          <ProgressIndicator />
        </div>
      ) : (
        <form
          id="lead-detail-view"
          ref={formRef}
          method="post"
          onSubmit={handleSubmit}
        >
          <Container maxWidth={false} disableGutters={true}>
            {/* Buttons */}
            <Grid container sx={{ width: "100%" }}>
              <Grid size={12} style={styles.alignRight}>
                <Box pt={3}>
                  {/* Archive lead */}
                  <Button
                    onClick={(_) => {
                      Swal.fire({
                        title: "Archiveren",
                        text: "Weet je zeker dat je de aanvraag wilt archiveren?",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#ec2027",
                        confirmButtonText: "Archiveren",
                      }).then((result) => {
                        if (result.value) {
                          setLoading(true);
                          archiveRentalPriceCalculation();
                        }
                      });
                    }}
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    style={styles.defaultButton}
                  >
                    {isTabletOrMobileDevice ? (
                      <span />
                    ) : (
                      <span>Archiveren</span>
                    )}
                  </Button>

                  {/* Save */}
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    edge="end"
                    style={styles.saveButton}
                  >
                    {isTabletOrMobileDevice ? <span /> : <span>Opslaan</span>}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* General info*/}
            <Grid container spacing={2}>
              <Grid size={12}>
                <Paper>
                  <Box p={3}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 7 }}>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <Typography variant="h4" gutterBottom={true}>
                              Aanvraag huurprijsberekening
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              color={"textSecondary"}
                            >
                              {"Datum aanvraag: " +
                                formatDisplayDate(data.leadRegistrationDate)}
                            </Typography>
                            {data.leadIsConfirmed === true ? (
                              <Typography>
                                <span style={styles.active}>
                                  Aanvraag is bevestigd
                                </span>
                              </Typography>
                            ) : (
                              <Box mt={8}>
                                <Typography paragraph={true}>
                                  <span style={styles.inactive}>
                                    Aanvraag is nog niet bevestigd
                                  </span>
                                </Typography>{" "}
                                {/* Send activation mail */}
                                <Button
                                  onClick={(_) =>
                                    Swal.fire({
                                      title: "",
                                      text: `E-mail versturen naar ${data.leadApplicationUserEmail}?`,
                                      icon: "info",
                                      showCancelButton: true,
                                      confirmButtonColor: "#eb008b",
                                      confirmButtonText: "Versturen",
                                    }).then((result) => {
                                      if (result.value) {
                                        sendAccountActivationEmail();
                                      }
                                    })
                                  }
                                  variant="contained"
                                  style={styles.mailButton}
                                  startIcon={<MailIcon />}
                                >
                                  {isTabletOrMobileDevice ? (
                                    <span />
                                  ) : (
                                    <span>Stuur activatie mail</span>
                                  )}
                                </Button>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={{ xs: 12, md: 5 }}>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  onChange={handleInputChange}
                                  label="Voorgestelde huurprijs"
                                  variant={textFieldVariant}
                                  name="proposedRentalPrice"
                                  type="number"
                                  defaultValue={
                                    data.proposedRentalPrice !== null
                                      ? data.proposedRentalPrice
                                      : 0
                                  }
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <LocalizationProvider
                                  dateAdapter={AdapterMoment}
                                  adapterLocale="nl"
                                >
                                  <DatePicker
                                    label="Voorgesteld op"
                                    value={
                                      data.dateRentalPriceProposedOn
                                        ? moment(data.dateRentalPriceProposedOn)
                                        : null
                                    }
                                    format="DD/MM/YYYY"
                                    onChange={(m) => {
                                      if (m && m.isValid()) {
                                        const utc = new Date(
                                          Date.UTC(
                                            m.year(),
                                            m.month(),
                                            m.date(),
                                            0,
                                            0,
                                            0,
                                            0
                                          )
                                        );
                                        handleDateChange(
                                          "dateRentalPriceProposedOn",
                                          utc.toISOString()
                                        );
                                      } else if (!m) {
                                        handleDateChange(
                                          "dateRentalPriceProposedOn",
                                          null
                                        );
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        sx: {
                                          "& .MuiInputBase-root": {
                                            backgroundColor: "transparent",
                                          },
                                          "& .MuiFormLabel-root": {
                                            backgroundColor: "transparent",
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={12}>
                                <TextField
                                  onChange={handleInputChange}
                                  name="shbNotes"
                                  multiline={true}
                                  minRows={5}
                                  variant={textFieldVariant}
                                  label="Eigen notities"
                                  defaultValue={
                                    data.shbNotes !== null ? data.shbNotes : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Rental Price Request info */}
            <Grid container spacing={2}>
              {/* Contact data lead */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper>
                  <Box p={3}>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Typography variant="h6">Aanvrager</Typography>
                      </Grid>
                      <Grid size={12}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl variant="outlined" fullWidth>
                              <InputLabel>Aanhef*</InputLabel>
                              <Select
                                required
                                label="Aanhef"
                                type="string"
                                name="leadApplicationUserSalutation"
                                value={
                                  data.leadApplicationUserSalutation !== null
                                    ? data.leadApplicationUserSalutation
                                    : ""
                                }
                                onChange={handleInputChange}
                                fullWidth
                              >
                                <MenuItem value="meneer">Meneer</MenuItem>
                                <MenuItem value="mevrouw">Mevrouw</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              required
                              onChange={handleInputChange}
                              variant={textFieldVariant}
                              fullWidth
                              label="Voornaam"
                              name="leadApplicationUserFirstName"
                              defaultValue={
                                data.leadApplicationUserFirstName !== null
                                  ? data.leadApplicationUserFirstName
                                  : ""
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={12}>
                        <TextField
                          required
                          onChange={handleInputChange}
                          variant={textFieldVariant}
                          fullWidth
                          label="Achternaam"
                          name="leadApplicationUserLastName"
                          defaultValue={
                            data.leadApplicationUserLastName !== null
                              ? data.leadApplicationUserLastName
                              : ""
                          }
                        />
                      </Grid>
                      <Grid size={12}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl variant="outlined" fullWidth>
                              <InputLabel>Eigenaar van het pand*</InputLabel>
                              <Select
                                label="Eigenaar van het pand"
                                type="string"
                                name="isOwner"
                                value={
                                  data.isOwner !== null ? data.isOwner : "Ja"
                                }
                                onChange={handleInputChange}
                                fullWidth
                              >
                                <MenuItem value="Ja">Ja</MenuItem>
                                <MenuItem value="Nee">Nee</MenuItem>
                                <MenuItem value="Binnenkort">
                                  Binnenkort
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              required
                              onChange={handleInputChange}
                              variant={textFieldVariant}
                              fullWidth
                              label="Telefoonnummer"
                              name="leadApplicationUserPhoneNumber"
                              defaultValue={
                                data.leadApplicationUserPhoneNumber !== null
                                  ? data.leadApplicationUserPhoneNumber
                                  : ""
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={12}>
                        <TextField
                          required
                          disabled
                          variant={textFieldVariant}
                          fullWidth
                          label="E-mail"
                          name="leadApplicationUserEmail"
                          defaultValue={
                            data.leadApplicationUserEmail !== null
                              ? data.leadApplicationUserEmail
                              : ""
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>

              {/* Property info */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Paper>
                  <Box p={3}>
                    <Grid container spacing={2}>
                      {/* Header */}
                      <Grid size={12}>
                        <Grid container spacing={2}>
                          <Grid>
                            <Typography variant="h6">
                              Informatie huurwoning
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* General info */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  required
                                  onChange={handleInputChange}
                                  label="Aantal m²"
                                  variant={textFieldVariant}
                                  type="number"
                                  name="surfaceInSquareMeters"
                                  defaultValue={
                                    data.surfaceInSquareMeters !== null
                                      ? data.surfaceInSquareMeters
                                      : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  required
                                  onChange={handleInputChange}
                                  label="Aantal slaapkamers"
                                  variant={textFieldVariant}
                                  type="number"
                                  name="numberOfBedrooms"
                                  defaultValue={
                                    data.numberOfBedrooms !== null
                                      ? data.numberOfBedrooms
                                      : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={12}>
                                <FormControl variant="outlined" fullWidth>
                                  <InputLabel>Oplevering*</InputLabel>
                                  <Select
                                    label="Oplevering"
                                    type="string"
                                    name="interior"
                                    value={
                                      data.interior !== null
                                        ? data.interior
                                        : ""
                                    }
                                    onChange={handleInputChange}
                                    fullWidth
                                  >
                                    <MenuItem value="Gemeubileerd">
                                      Gemeubileerd
                                    </MenuItem>
                                    <MenuItem value="Ongemeubileerd">
                                      Ongemeubileerd
                                    </MenuItem>
                                    <MenuItem value="Beide mogelijk">
                                      Beide mogelijk
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={12}>
                                <TextField
                                  onChange={handleInputChange}
                                  name="extraInfo"
                                  multiline={true}
                                  minRows={5}
                                  variant={textFieldVariant}
                                  label="Extra informatie"
                                  defaultValue={
                                    data.extraInfo !== null
                                      ? data.extraInfo
                                      : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* Address */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={12}>
                                <TextField
                                  required
                                  onChange={handleInputChange}
                                  variant={textFieldVariant}
                                  label="Straat"
                                  name="street"
                                  defaultValue={
                                    data.street !== null ? data.street : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <TextField
                                  required
                                  onChange={handleInputChange}
                                  label="Huisnummer"
                                  variant={textFieldVariant}
                                  type="number"
                                  name="number"
                                  defaultValue={
                                    data.number !== null ? data.number : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <TextField
                                  onChange={handleInputChange}
                                  label="Toevoeging"
                                  variant={textFieldVariant}
                                  name="addition"
                                  defaultValue={
                                    data.addition !== null ? data.addition : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <TextField
                                  required
                                  onChange={handleInputChange}
                                  variant={textFieldVariant}
                                  label="Postcode"
                                  name="zipCode"
                                  defaultValue={
                                    data.zipCode !== null ? data.zipCode : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid size={12}>
                            <Grid container spacing={2}>
                              <Grid size={12}>
                                <TextField
                                  required
                                  onChange={handleInputChange}
                                  variant={textFieldVariant}
                                  label="Plaats"
                                  name="city"
                                  defaultValue={
                                    data.city !== null ? data.city : ""
                                  }
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
          <Container maxWidth={false} disableGutters={true}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Paper>
                  <Box p={3}>
                    <Grid container spacing={2}>
                      <Grid>
                        <Typography gutterBottom={false} variant="h6">
                          Foto's
                        </Typography>
                      </Grid>
                      <Grid size={12}>
                        <PhotoGallery
                          files={data.fileDocuments.filter(
                            (e) =>
                              e.fileCategory ===
                              fileCategories.rentalPriceCalculationImage
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper>
                  <Box p={3}>
                    <Grid container spacing={2}>
                      <Grid>
                        <Typography gutterBottom={false} variant="h6">
                          Plattegrond
                        </Typography>
                      </Grid>
                      <Grid size={12}>
                        <PhotoGallery
                          files={data.fileDocuments.filter(
                            (e) =>
                              e.fileCategory ===
                              fileCategories.rentalPriceCalculationFloorPlan
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </form>
      )}
    </Fragment>
  );
}
